/**
 * Client Services Page
 * View and manage active services
 */

import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  Server,
  Globe,
  Clock,
  DollarSign,
  AlertCircle,
  CheckCircle,
  XCircle,
  RefreshCw,
  Settings,
  ExternalLink,
} from 'lucide-react';

interface Service {
  id: number;
  productId: number;
  productName: string;
  domain?: string;
  username?: string;
  billingCycle: string;
  recurringAmount: number;
  status: 'active' | 'suspended' | 'terminated' | 'pending' | 'cancelled';
  nextDueDate: string;
  registrationDate: string;
}

const statusConfig = {
  active: {
    label: 'Active',
    icon: CheckCircle,
    color: 'text-green-400',
    bgColor: 'bg-green-900/20',
    borderColor: 'border-green-700',
  },
  pending: {
    label: 'Pending',
    icon: Clock,
    color: 'text-yellow-400',
    bgColor: 'bg-yellow-900/20',
    borderColor: 'border-yellow-700',
  },
  suspended: {
    label: 'Suspended',
    icon: AlertCircle,
    color: 'text-orange-400',
    bgColor: 'bg-orange-900/20',
    borderColor: 'border-orange-700',
  },
  terminated: {
    label: 'Terminated',
    icon: XCircle,
    color: 'text-red-400',
    bgColor: 'bg-red-900/20',
    borderColor: 'border-red-700',
  },
  cancelled: {
    label: 'Cancelled',
    icon: XCircle,
    color: 'text-gray-400',
    bgColor: 'bg-gray-900/20',
    borderColor: 'border-gray-700',
  },
};

const ServicesPage: React.FC = () => {
  const [services, setServices] = useState<Service[]>([]);
  const [loading, setLoading] = useState(true);
  const [filterStatus, setFilterStatus] = useState<string>('all');

  useEffect(() => {
    loadServices();
  }, []);

  const loadServices = async () => {
    setLoading(true);
    try {
      const response = await fetch('/api/v1/client/services', {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`,
        },
      });
      const data = await response.json();
      if (data.success) {
        setServices(data.data.services);
      }
    } catch (error) {
      console.error('Failed to load services:', error);
    } finally {
      setLoading(false);
    }
  };

  const filteredServices = services.filter(
    (service) => filterStatus === 'all' || service.status === filterStatus
  );

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const formatPrice = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(amount);
  };

  const getBillingCycleLabel = (cycle: string) => {
    const labels: Record<string, string> = {
      monthly: 'Monthly',
      quarterly: 'Quarterly',
      semiannually: 'Semi-Annually',
      annually: 'Annually',
      biennially: 'Biennially',
      triennially: 'Triennially',
    };
    return labels[cycle] || cycle;
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold text-white flex items-center gap-3">
          <Server className="w-8 h-8" />
          My Services
        </h1>
        <p className="text-gray-400 mt-2">Manage your active hosting services and products</p>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 animate-fade-in">
        <div className="bg-slate-800/80 backdrop-blur-sm rounded-xl p-4 border border-slate-700/50 hover:border-orange-500/30 transition-all duration-300">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-slate-400 text-sm">Total Services</p>
              <p className="text-2xl font-bold text-white mt-1">{services.length}</p>
            </div>
            <div className="w-10 h-10 bg-orange-500/10 rounded-lg flex items-center justify-center">
              <Server className="w-5 h-5 text-orange-400" />
            </div>
          </div>
        </div>

        <div className="bg-slate-800/80 backdrop-blur-sm rounded-xl p-4 border border-slate-700/50 hover:border-green-500/30 transition-all duration-300">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-slate-400 text-sm">Active</p>
              <p className="text-2xl font-bold text-green-400 mt-1">
                {services.filter((s) => s.status === 'active').length}
              </p>
            </div>
            <div className="w-10 h-10 bg-green-500/10 rounded-lg flex items-center justify-center">
              <CheckCircle className="w-5 h-5 text-green-400" />
            </div>
          </div>
        </div>

        <div className="bg-slate-800/80 backdrop-blur-sm rounded-xl p-4 border border-slate-700/50 hover:border-yellow-500/30 transition-all duration-300">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-slate-400 text-sm">Pending</p>
              <p className="text-2xl font-bold text-yellow-400 mt-1">
                {services.filter((s) => s.status === 'pending').length}
              </p>
            </div>
            <div className="w-10 h-10 bg-yellow-500/10 rounded-lg flex items-center justify-center">
              <Clock className="w-5 h-5 text-yellow-400" />
            </div>
          </div>
        </div>

        <div className="bg-slate-800/80 backdrop-blur-sm rounded-xl p-4 border border-slate-700/50 hover:border-orange-500/30 transition-all duration-300">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-slate-400 text-sm">Suspended</p>
              <p className="text-2xl font-bold text-orange-400 mt-1">
                {services.filter((s) => s.status === 'suspended').length}
              </p>
            </div>
            <div className="w-10 h-10 bg-orange-500/10 rounded-lg flex items-center justify-center">
              <AlertCircle className="w-5 h-5 text-orange-400" />
            </div>
          </div>
        </div>
      </div>

      {/* Filter Tabs */}
      <div className="flex flex-wrap gap-2">
        {['all', 'active', 'pending', 'suspended', 'cancelled'].map((status) => (
          <button
            key={status}
            onClick={() => setFilterStatus(status)}
            className={`px-4 py-2 rounded-lg font-medium transition-all duration-300 ${
              filterStatus === status
                ? 'bg-orange-500 text-white shadow-lg shadow-orange-500/25'
                : 'bg-slate-800/80 text-slate-400 hover:bg-slate-700/50 hover:text-white border border-slate-700/50'
            }`}
          >
            {status.charAt(0).toUpperCase() + status.slice(1)}
            {status !== 'all' && (
              <span className="ml-2 text-xs">
                ({services.filter((s) => s.status === status).length})
              </span>
            )}
          </button>
        ))}
      </div>

      {/* Services List */}
      {loading ? (
        <div className="bg-slate-800/80 backdrop-blur-sm rounded-xl p-12 border border-slate-700/50">
          <div className="flex items-center justify-center">
            <RefreshCw className="w-8 h-8 text-orange-400 animate-spin" />
            <span className="ml-3 text-slate-400">Loading services...</span>
          </div>
        </div>
      ) : filteredServices.length === 0 ? (
        <div className="bg-slate-800/80 backdrop-blur-sm rounded-xl p-12 border border-slate-700/50 text-center">
          <Server className="w-16 h-16 text-slate-600 mx-auto mb-4" />
          <h3 className="text-xl font-medium text-white mb-2">No Services Found</h3>
          <p className="text-slate-400 mb-6">
            {filterStatus === 'all'
              ? "You don't have any services yet."
              : `You don't have any ${filterStatus} services.`}
          </p>
          <Link
            to="/client/cart"
            className="inline-flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-orange-500 to-orange-600 text-white rounded-lg hover:from-orange-600 hover:to-orange-700 transition-all duration-300 shadow-lg shadow-orange-500/25"
          >
            <Server className="w-5 h-5" />
            Browse Products
          </Link>
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-4">
          {filteredServices.map((service) => {
            const statusInfo = statusConfig[service.status];
            const StatusIcon = statusInfo.icon;

            return (
              <div
                key={service.id}
                className={`bg-slate-800/80 backdrop-blur-sm rounded-xl border ${statusInfo.borderColor} hover:border-orange-500/50 transition-all duration-300`}
              >
                <div className="p-6">
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <h3 className="text-xl font-bold text-white">{service.productName}</h3>
                        <span
                          className={`flex items-center gap-1.5 px-3 py-1 rounded-full text-sm font-medium ${statusInfo.bgColor} ${statusInfo.color}`}
                        >
                          <StatusIcon className="w-4 h-4" />
                          {statusInfo.label}
                        </span>
                      </div>
                      {service.domain && (
                        <div className="flex items-center gap-2 text-gray-400 text-sm">
                          <Globe className="w-4 h-4" />
                          {service.domain}
                        </div>
                      )}
                      {service.username && (
                        <div className="text-gray-400 text-sm mt-1">
                          Username: <span className="text-white">{service.username}</span>
                        </div>
                      )}
                    </div>
                    <Link
                      to={`/client/services/${service.id}`}
                      className="px-4 py-2 bg-gradient-to-r from-orange-500 to-orange-600 text-white rounded-lg hover:from-orange-600 hover:to-orange-700 transition-all duration-300 flex items-center gap-2 shadow-lg shadow-orange-500/25"
                    >
                      <Settings className="w-4 h-4" />
                      Manage
                    </Link>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4 pt-4 border-t border-slate-700/50">
                    <div>
                      <p className="text-gray-400 text-sm mb-1">Billing Cycle</p>
                      <p className="text-white font-medium">
                        {getBillingCycleLabel(service.billingCycle)}
                      </p>
                    </div>
                    <div>
                      <p className="text-gray-400 text-sm mb-1">Amount</p>
                      <p className="text-white font-medium flex items-center gap-1">
                        <DollarSign className="w-4 h-4" />
                        {formatPrice(service.recurringAmount)}
                      </p>
                    </div>
                    <div>
                      <p className="text-gray-400 text-sm mb-1">Next Due Date</p>
                      <p className="text-white font-medium flex items-center gap-1">
                        <Clock className="w-4 h-4" />
                        {formatDate(service.nextDueDate)}
                      </p>
                    </div>
                  </div>

                  {service.status === 'active' && (
                    <div className="mt-4 pt-4 border-t border-slate-700/50">
                      <div className="flex flex-wrap gap-2">
                        <button className="px-3 py-1.5 bg-slate-700/50 text-white rounded-lg text-sm hover:bg-slate-600/50 transition-all duration-300 flex items-center gap-2">
                          <ExternalLink className="w-4 h-4" />
                          Control Panel
                        </button>
                        <button className="px-3 py-1.5 bg-slate-700/50 text-white rounded-lg text-sm hover:bg-slate-600/50 transition-all duration-300">
                          Change Password
                        </button>
                        <button className="px-3 py-1.5 bg-slate-700/50 text-white rounded-lg text-sm hover:bg-slate-600/50 transition-all duration-300">
                          Upgrade/Downgrade
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default ServicesPage;
